// /home/coder/project/frontend/src/pages/core/CorUnitsList.tsx
import React, { useMemo, useState } from 'react';
import { Spin, Alert, Typography, Space, Divider, Row, Col, Input, Select, Drawer, Form, AutoComplete, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SearchOutlined, SaveOutlined } from '@ant-design/icons';
import { GiWeight } from 'react-icons/gi';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CorUnits } from '../../types';
import { useGetUnits } from '../../hooks/genericHooks';
import { insertData, updateData } from '../../services/api';
import StandardTable, { createActionColumn } from '../../components/common/StandardTable';
import Button from '../../components/common/Button';

const { Title } = Typography;

const RGiWeight: React.FC<any> = (props) =>
  React.createElement(GiWeight as unknown as React.ComponentType<any>, props) as React.ReactElement;

type CorUnitFormData = Pick<CorUnits, 'name' | 'category' | 'symbol' | 'description'>;

const emptyUnit: CorUnitFormData = {
  name: '',
  category: '',
  symbol: '',
  description: '',
};

function unitPayload(values: CorUnitFormData) {
  return {
    name: values.name.trim(),
    symbol: values.symbol.trim(),
    category: values.category?.trim() || null,
    description: values.description?.trim() || null,
  };
}

function serverErrorText(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  return data?.error || data?.message || (err instanceof Error ? err.message : fallback);
}

const CorUnitsList: React.FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CorUnitFormData>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<CorUnits | null>(null);

  // --- State for filters ---
  const [nameSearch, setNameSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [symbolSearch, setSymbolSearch] = useState('');

  // Fetch data using the custom hook
  const { data: unitsResult, isLoading, error, isError } = useGetUnits();
  const units = useMemo(() => unitsResult?.data || [], [unitsResult]);

  // --- Calculate Unique Categories for Select Options ---
  const categoryOptions = useMemo(() => {
    if (!units) return [];
    const uniqueCategories = Array.from(
      new Set(units.map(item => item.category).filter(Boolean) as string[])
    ).sort();
    const options = uniqueCategories.map(cat => ({ label: cat, value: cat }));
    return [{ label: 'All Categories', value: '' }, ...options]; // Add "All" option with empty value
  }, [units]);

  // --- Filtering Logic ---
  const filteredUnits = useMemo(() => {
    if (!units) return [];
    const lowerName = nameSearch.toLowerCase();
    const lowerCategory = categorySearch.toLowerCase();
    const lowerSymbol = symbolSearch.toLowerCase();
    return units.filter(unit =>
      (!lowerName || unit.name.toLowerCase().includes(lowerName)) &&
      (!categorySearch || unit.category === categorySearch) && // Exact match for Select filter
      (!lowerSymbol || unit.symbol.toLowerCase().includes(lowerSymbol))
    );
  }, [units, nameSearch, categorySearch, symbolSearch]);

  const categorySuggestions = useMemo(() => {
    const categories = units
      .map((item) => item.category)
      .filter((category): category is string => !!category);
    return Array.from(new Set(categories)).map((category) => ({ value: category }));
  }, [units]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue(emptyUnit);
    setDrawerOpen(true);
  };

  const openEdit = (record: CorUnits) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      category: record.category || '',
      symbol: record.symbol,
      description: record.description || '',
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const { mutate: saveUnit, isPending: isSaving } = useMutation<unknown, Error, CorUnitFormData>({
    mutationFn: (values) => {
      const payload = unitPayload(values);
      if (editing) return updateData('cor_units', editing.id, payload);
      return insertData('cor_units', payload);
    },
    onSuccess: (_response, values) => {
      queryClient.invalidateQueries({ queryKey: ['cor_units'] });
      message.success(editing ? `Unit "${values.name}" updated successfully.` : `Unit "${values.name}" created successfully.`);
      closeDrawer();
    },
    onError: (err) => {
      message.error(serverErrorText(err, editing ? 'Failed to update unit.' : 'Failed to create unit.'));
    },
  });

  // Define table columns
  const columns: ColumnsType<CorUnits> = [ // Explicitly type the columns array
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: CorUnits, b: CorUnits) => a.id - b.id,
      align: 'right',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: CorUnits, b: CorUnits) => a.name.localeCompare(b.name),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      // Add built-in filters similar to CorFoodIngredientsList
      filters: categoryOptions
                 .filter(opt => opt.value !== '') // Exclude "All Categories"
                 .map(opt => ({ text: opt.label, value: opt.value })),
      // Use Ant Design's onFilter for the column filter
      onFilter: (value, record) => record.category === value,
      sorter: (a: CorUnits, b: CorUnits) => a.category.localeCompare(b.category),
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 100,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true, // Shorten long descriptions
    },
    createActionColumn<CorUnits>({
      title: 'Actions',
      align: 'center',
      showView: false,
      showDelete: false,
      onEdit: (record) => openEdit(record),
    }),
  ];

  return (
    <>
      <Divider style={{ marginTop: 0 }} />
      <div style={{ padding: '0 24px' }}>
        {/* Header Row */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <RGiWeight size={22} style={{ marginRight: 8 }} /> Units Management
            </Title>
          </Col>
          <Col>
            <Button action="add" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Create New Unit
            </Button>
          </Col>
        </Row>

        {/* Filter Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input placeholder="Search by Name" prefix={<SearchOutlined />} value={nameSearch} onChange={(e) => setNameSearch(e.target.value)} allowClear />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            {/* Changed Input to Select for Category */}
            <Select
              showSearch
              placeholder="Select Category"
              value={categorySearch}
              onChange={(value) => setCategorySearch(value ?? '')} // Handle clear correctly
              style={{ width: '100%' }}
              options={categoryOptions}
              allowClear
              loading={isLoading} // Show loading state if categories depend on data fetch
              optionFilterProp="label" // Allow searching by label in the dropdown
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input placeholder="Search by Symbol" prefix={<SearchOutlined />} value={symbolSearch} onChange={(e) => setSymbolSearch(e.target.value)} allowClear />
          </Col>
        </Row>

        {isError && <Alert message="Error fetching units" description={error?.message || 'Could not fetch data.'} type="error" showIcon closable style={{ marginBottom: 16 }} />}

        <Spin spinning={isLoading}>
          <StandardTable
            columns={columns as any}
            dataSource={filteredUnits}
            rowKey="id"
            loading={isLoading}
            bordered
            size="small"
            pagination={{ pageSize: 15 }}
            scroll={{ x: 'max-content' }}
            enableExport
          />
        </Spin>

        <Drawer
          title={editing ? `Edit Unit: ${editing.name}` : 'Create New Unit'}
          open={drawerOpen}
          onClose={closeDrawer}
          width={560}
          destroyOnClose
          afterOpenChange={(visible) => {
            if (!visible) return;
            if (editing) {
              form.setFieldsValue({
                name: editing.name,
                category: editing.category || '',
                symbol: editing.symbol,
                description: editing.description || '',
              });
              return;
            }
            form.setFieldsValue(emptyUnit);
          }}
          extra={
            <Space>
              <Button onClick={closeDrawer} disabled={isSaving}>Cancel</Button>
              <Button action={editing ? 'edit' : 'add'} type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={() => form.submit()}>
                {editing ? 'Update Unit' : 'Create Unit'}
              </Button>
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => saveUnit(values)}
            initialValues={emptyUnit}
            autoComplete="off"
          >
            <Form.Item
              label="Unit Name"
              name="name"
              rules={[{ required: true, whitespace: true, message: 'Please enter the unit name' }]}
            >
              <Input placeholder="e.g., Kilogram, Liter, Piece" />
            </Form.Item>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, whitespace: true, message: 'Please select or enter a category' }]}
            >
              <AutoComplete
                options={categorySuggestions}
                placeholder="Select or type category (e.g., Weight, Volume, Count)"
                filterOption={(inputValue, option) =>
                  option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
            </Form.Item>
            <Form.Item
              label="Symbol"
              name="symbol"
              rules={[{ required: true, whitespace: true, message: 'Please enter the unit symbol' }]}
            >
              <Input placeholder="e.g., kg, L, pcs" />
            </Form.Item>
            <Form.Item label="Description" name="description">
              <Input.TextArea rows={3} placeholder="Optional: Describe the unit or its usage" />
            </Form.Item>
          </Form>
        </Drawer>
      </div>
    </>
  );
};

export default CorUnitsList;
